import React from 'react';
import { Box, Button } from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { formattedUserFacingDateTime } from 'DateHelpers';
import { type ColumnDefinition } from '../api';
import { GraderAssignmentCell } from '../SubmissionsTable/GraderAssignmentCell';
import { formatUrgencyMarks, formatGradingStatus, formatSimilarityAndOriginalityStatus } from './formatters';
import {
    type EvaluationColumnOptions,
    type AssignedGraderColumnOptions,
    type SaoCheckColumnOptions,
} from './columnDefinitions.types';
import { SaoCheckCell } from '../SaoCheckDetail/SaoCheckCell';

// --- Static Column Factories ---

/**
 * Creates the Urgency column definition.
 * Displays exclamation marks based on urgency score with tooltips that indicate
 * the reason for urgency (graduation timing, resubmission, or capstone).
 */
export function createUrgencyColumn(): ColumnDefinition {
    return {
        id: 'urgencyScore',
        label: 'Urgency',
        sortable: true,
        sortKey: 'urgency_score',
        width: 70,
        render: s => formatUrgencyMarks(s.urgencyScore, s.isResubmission, s.isCapstoneOrCapstonePresentation),
    };
}

/**
 * Creates the Attempt column definition.
 * Displays the submission attempt number (1 = initial submission, 2+ = resubmission).
 */
export function createAttemptNumberColumn(): ColumnDefinition {
    return {
        id: 'submissionAttemptNumber',
        label: 'Attempt',
        sortable: true,
        sortKey: 'submission_attempt_number',
        width: 80,
        render: s => s.submissionAttemptNumber,
    };
}

export function createGradingStatusColumn(): ColumnDefinition {
    return {
        id: 'gradingStatus',
        label: 'Grading Status',
        sortable: true,
        sortKey: 'grading_status',
        width: 140,
        render: s => formatGradingStatus(s.gradingStatus),
    };
}

export function createCohortColumn(): ColumnDefinition {
    return {
        id: 'cohortName',
        label: 'Cohort',
        sortable: true,
        sortKey: 'cohort_name',
        width: 150,
        render: s => s.cohortName?.name ?? '-',
    };
}

export function createRequirementIdentifierColumn(): ColumnDefinition {
    return {
        id: 'requirementIdentifier',
        label: 'Requirement',
        sortable: true,
        sortKey: 'requirement_identifier',
        render: s => s.requirementIdentifier,
    };
}

export function createStudentNameColumn(): ColumnDefinition {
    return {
        id: 'studentName',
        label: 'Submitter',
        sortable: true,
        sortKey: 'student_name',
        width: 150,
        render: s => s.student.name,
    };
}

export function createStudentEmailColumn(): ColumnDefinition {
    return {
        id: 'studentEmail',
        label: 'Email',
        sortable: true,
        sortKey: 'student_email',
        width: 180,
        render: s => s.student.email,
    };
}

/**
 * Creates the Group Members column definition.
 * Shows group member names for group submissions, or '-' for individual submissions.
 */
export function createGroupMembersColumn(): ColumnDefinition {
    return {
        id: 'groupMembers',
        label: 'Group Members',
        render: s => (s.groupMembers.length > 1 ? s.groupMembers.map(m => m.name).join(', ') : '-'),
    };
}

export function createSubmittedAtColumn(): ColumnDefinition {
    return {
        id: 'submittedAt',
        label: 'Submitted',
        sortable: true,
        sortKey: 'submitted_at',
        width: 140,
        render: s => formattedUserFacingDateTime(s.submittedAt * 1000),
    };
}

/**
 * Creates the Date Graded column definition.
 * Displays the date when the submission was graded (completed).
 * Only available for completed submissions.
 */
export function createDateGradedColumn(): ColumnDefinition {
    return {
        id: 'dateGraded',
        label: 'Date Graded',
        sortable: true,
        sortKey: 'date_graded',
        width: 140,
        render: s => (s.dateGraded ? formattedUserFacingDateTime(s.dateGraded * 1000) : '-'),
    };
}

export function createDueDateColumn(): ColumnDefinition {
    return {
        id: 'dueDate',
        label: 'Due Date',
        sortable: true,
        sortKey: 'due_date',
        width: 140,
        render: s => (s.dueDate ? formattedUserFacingDateTime(s.dueDate * 1000) : '-'),
    };
}

export function createDaysLateColumn(): ColumnDefinition {
    return {
        id: 'daysSubmittedAfterDeadline',
        label: 'Days Late',
        sortable: true,
        sortKey: 'days_submitted_after_deadline',
        width: 80,
        render: s => s.daysSubmittedAfterDeadline ?? '-',
    };
}

export function createDaysSinceSubmitColumn(): ColumnDefinition {
    return {
        id: 'daysSinceSubmit',
        label: 'Days Since Submit',
        sortable: true,
        sortKey: 'days_since_submit',
        width: 80,
        render: s => s.daysSinceSubmit ?? '-',
    };
}

export function createDaysToGraduationColumn(): ColumnDefinition {
    return {
        id: 'daysUntilGraduation',
        label: 'Days to Grad',
        sortable: true,
        sortKey: 'days_until_graduation',
        width: 100,
        render: s => s.daysUntilGraduation ?? '-',
    };
}

/**
 * Creates the Student ID column definition.
 * Displays the user's UUID for matching with external systems (e.g., Turnitin).
 */
export function createStudentIdColumn(): ColumnDefinition {
    return {
        id: 'studentId',
        label: 'Student ID',
        sortable: true,
        sortKey: 'student_id',
        width: 280,
        render: s => (
            <Box
                component="span"
                sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    wordBreak: 'break-all',
                }}
            >
                {s.student.id}
            </Box>
        ),
    };
}

export function createSAndOStatusColumn(): ColumnDefinition {
    return {
        id: 'similarityAndOriginalityStatus',
        label: 'S&O Status',
        width: 160,
        render: s => formatSimilarityAndOriginalityStatus(s.similarityAndOriginalityStatus),
    };
}

/**
 * Creates the Processed At column definition.
 * Displays when the S&O status was first changed from 'not_checked'.
 */
export function createProcessedAtColumn(): ColumnDefinition {
    return {
        id: 'processedAt',
        label: 'Processed At',
        sortable: true,
        sortKey: 'processed_at',
        width: 140,
        render: s =>
            s.similarityAndOriginalityStatusFirstUpdatedAt
                ? formattedUserFacingDateTime(s.similarityAndOriginalityStatusFirstUpdatedAt * 1000)
                : '-',
    };
}

/**
 * Creates the S&O Check column definition.
 * Displays per-file status badges. When clicked, opens the detail modal.
 */
export function createSaoCheckColumn(options: SaoCheckColumnOptions): ColumnDefinition {
    const { onSaoCheckClick } = options;
    return {
        id: 'saoCheck',
        label: 'S&O Check',
        width: 140,
        render: s => <SaoCheckCell submission={s} onSaoCheckClick={onSaoCheckClick} />,
    };
}

// --- Dynamic Column Factories ---

/**
 * Creates the Evaluation column definition.
 * Displays an "Open" button that opens the evaluation modal.
 *
 * @param options - Configuration for the evaluation column
 */
export function createEvaluationColumn(options: EvaluationColumnOptions): ColumnDefinition {
    const { onEvaluationClick, showNeedsReviewIndicator = false, width = 100 } = options;

    return {
        id: 'evaluationLink',
        label: 'Evaluation',
        width,
        render: s => {
            const button = (
                <Button
                    size="small"
                    variant="text"
                    startIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={() => onEvaluationClick(s)}
                    sx={{ color: '#1976d2 !important' }}
                >
                    Open
                </Button>
            );

            if (showNeedsReviewIndicator && s.needsAdminReview) {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {button}
                        <span style={{ color: '#d32f2f', fontSize: '0.75rem', fontWeight: 500 }}>(Needs Review)</span>
                    </Box>
                );
            }

            return button;
        },
    };
}

/**
 * Creates the Assigned Grader column definition.
 * Displays the GraderAssignmentCell component for assigning/viewing grader assignment,
 * or a read-only grader name when readOnly is true.
 *
 * @param options - Configuration for the assigned grader column
 */
export function createAssignedGraderColumn(options: AssignedGraderColumnOptions = {}): ColumnDefinition {
    const { onProjectIdentifierAutoAssigned, readOnly } = options;

    return {
        id: 'assignedStatus',
        label: 'Assigned',
        sortable: true,
        sortKey: 'assigned_status',
        width: 170,
        render: s => {
            if (readOnly) {
                return s.assignedStatus?.graderName ?? '-';
            }
            return (
                <GraderAssignmentCell
                    submission={s}
                    onProjectIdentifierAutoAssigned={onProjectIdentifierAutoAssigned}
                />
            );
        },
    };
}

// --- Composable Column Sets ---

/**
 * Student info columns subset.
 * Includes: Student Name, Email, Group Members
 */
export function createStudentInfoColumns(): ColumnDefinition[] {
    return [createStudentNameColumn(), createStudentEmailColumn(), createGroupMembersColumn()];
}

/**
 * Admin-specific columns for detailed submission views.
 * Includes: Submitted At, Due Date, Days Since Submit, Days Late, Days to Graduation
 */
export function createAdminDetailColumns(): ColumnDefinition[] {
    return [
        createSubmittedAtColumn(),
        createDueDateColumn(),
        createDaysSinceSubmitColumn(),
        createDaysLateColumn(),
        createDaysToGraduationColumn(),
    ];
}
